import { useMemo, useEffect } from 'react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { useEditor, EditorContent } from '@tiptap/react';
import MarkdownIt from 'markdown-it';

import { FootnoteListNode } from '../../text-cell/extensions/nodes/footnote-list-node';
import { createFootnoteNode } from '../../text-cell/extensions/nodes/footnote-node';
import {
  ensureFootnoteFormatting,
  escapeAttributes,
  htmlCustomTags,
} from '../../text-cell/utils/markdown';

import styles from './status-message-content.module.scss';

// Markdown parser configured for footnote tags
const md = MarkdownIt({
  breaks: true,
  html: true,
}).use(htmlCustomTags);

// Check if content contains footnote tags
const hasFootnotes = (content: string | undefined): boolean =>
  content !== undefined && content.includes('<footnote-node');

// Convert content with footnote tags to HTML for TipTap
const convertContent = (content: string) =>
  md.render(escapeAttributes(ensureFootnoteFormatting(content)));

interface StatusMessageContentProps {
  content: string | undefined;
  uniqueId: string;
}

/**
 * Renders status message content with TipTap when footnotes are present,
 * otherwise renders plain text for performance.
 */
export const StatusMessageContent = ({ content, uniqueId }: StatusMessageContentProps) => {
  const shouldUseEditor = hasFootnotes(content);

  // Minimal extensions for read-only footnote rendering
  const extensions = useMemo(
    () => [Document, Paragraph, Text, FootnoteListNode, createFootnoteNode(uniqueId)],
    [uniqueId],
  );

  const editor = useEditor(
    {
      extensions,
      content: shouldUseEditor && content !== undefined ? convertContent(content) : '',
      editable: false,
    },
    [shouldUseEditor],
  );

  // Update editor content when it changes
  useEffect(() => {
    if (editor !== null && shouldUseEditor && content !== undefined) {
      editor.commands.setContent(convertContent(content));
    }
  }, [content, editor, shouldUseEditor]);

  // Render plain text when no footnotes present
  if (!shouldUseEditor) {
    return <>{content}</>;
  }

  return <EditorContent editor={editor} className={styles.richContent} />;
};
